// A one-off condition: an Item created from the takeoff canvas for ONE estimate (db/87 sp_CreateOneOffCondition). It is
// configured with the SAME drawable fields as a catalog Item (the shared DrawableOnCanvasFields block), plus a name, a
// cost code and a cost so it prices in the estimate. Quantity 1 is the unit it prices per.

import { calcTypeUnit } from "../config/conditionCalcTypes";

export const EMPTY_ONE_OFF = Object.freeze({
  name: "", costCodeId: null, material: "", labor: "", sub: "",
  // the Item's own drawable fields (PascalCase, as the shared form edits them)
  Style: "linear", Quantity1CalcType: "length", Quantity2CalcType: null, Quantity3CalcType: null,
  DefaultHeightFt: "", DefaultWidthFt: "", DefaultThicknessIn: "", Color: "#7c3aed", Pattern: null, Shape: null,
  SyncsToCriteriaField: null, AutoSystem: null, GridTileWFt: "", GridTileHFt: "", GridGapIn: "", PatternSpacingIn: "", RoundToIn: "", RoundAreaToSf: "",
});

const FALLBACK_UNIT = { linear: "LF", area: "SF", count: "EA" };

/** The unit a one-off prices per — what its Quantity 1 reading yields (SF for a surface-area reading, LF for a length…). */
export const oneOffUnit = (d = {}) => calcTypeUnit(d.Style, d.Quantity1CalcType) || FALLBACK_UNIT[d.Style] || "EA";

const pos = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0; };
const numOrNull = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };

/** What is still missing before the form can be saved (mirrors the proc's own checks). */
export function oneOffProblems(d = {}) {
  const out = [];
  if (!String(d.name ?? "").trim()) out.push("a name");
  if (!d.costCodeId) out.push("a cost code");
  if (!d.Quantity1CalcType) out.push("a Quantity 1 reading");
  if (!pos(d.material) && !pos(d.labor) && !pos(d.sub)) out.push("a cost (material, labor hours or subcontract)");
  return out;
}

/** The payload sp_CreateOneOffCondition takes (snake_case, like sp_SaveEstimateCostItem). "Generated system" is never sent. */
export function oneOffPayload(revisionId, d) {
  const num = (v) => (pos(v) ? parseFloat(v) : 0);
  const unitOf = (calc) => (calc ? calcTypeUnit(d.Style, calc) || null : null);
  return {
    revision_id: revisionId, name: String(d.name).trim(), style: d.Style, cost_code_id: d.costCodeId,
    material_cost_per_unit: num(d.material), labor_hr_per_unit: num(d.labor), vendor_sub_cost_per_unit: num(d.sub),
    uom: oneOffUnit(d).toLowerCase(),
    quantity1_calc_type: d.Quantity1CalcType, quantity2_calc_type: d.Quantity2CalcType || null, quantity3_calc_type: d.Quantity3CalcType || null,
    quantity2_unit: unitOf(d.Quantity2CalcType), quantity3_unit: unitOf(d.Quantity3CalcType),
    default_height_ft: numOrNull(d.DefaultHeightFt), default_width_ft: numOrNull(d.DefaultWidthFt), default_thickness_in: numOrNull(d.DefaultThicknessIn),
    color: d.Color || null, pattern: d.Pattern || null, shape: d.Shape || null, syncs_to_criteria_field: d.SyncsToCriteriaField || null,
    grid_tile_w_ft: numOrNull(d.GridTileWFt), grid_tile_h_ft: numOrNull(d.GridTileHFt), grid_gap_in: numOrNull(d.GridGapIn),
    pattern_spacing_in: numOrNull(d.PatternSpacingIn), round_to_in: numOrNull(d.RoundToIn), round_area_to_sf: numOrNull(d.RoundAreaToSf),
  };
}

/** "$12.50 mat + 0.2 hr" — what a one-off Item costs per unit, for the Setup list. */
export function oneOffCostText(row = {}) {
  return [
    Number(row.MaterialCostPerUnit) > 0 && `$${Number(row.MaterialCostPerUnit)} mat`,
    Number(row.LaborHrPerUnit) > 0 && `${Number(row.LaborHrPerUnit)} hr`,
    Number(row.VendorSubCostPerUnit) > 0 && `$${Number(row.VendorSubCostPerUnit)} sub`,
  ].filter(Boolean).join(" + ") || "—";
}
